import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  Insights as AnalyticsIcon,
  SportsEsports as PlayIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Header from '../Header';
import HigherOrLowerSpHostTab from './HigherOrLowerSpHostTab';
import { HOL } from '../../UserSide/pages/holTheme';
import { guardedNavigate } from '../../../utils/adminNavigationGuard';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const holTabsSx = {
  minHeight: 46,
  '& .MuiTabs-flexContainer': { gap: 0.5 },
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '3px 3px 0 0',
    background: `linear-gradient(90deg, ${HOL.higher}, ${HOL.gold})`,
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 700,
    fontFamily: HOL.fontDisplay,
    letterSpacing: '0.02em',
    minHeight: 46,
    color: 'rgba(255,255,255,0.5)',
    '&.Mui-selected': { color: '#fff' },
  },
};

const holPanelSx = {
  p: 2.5,
  bgcolor: HOL.panel,
  backdropFilter: 'blur(18px)',
  border: `1px solid ${HOL.panelBorder}`,
  borderRadius: 2.5,
  boxShadow: '0 10px 32px rgba(0,0,0,0.28)',
};

const holStatCardSx = (accent) => ({
  bgcolor: HOL.panel,
  backdropFilter: 'blur(18px)',
  border: `1px solid ${HOL.panelBorder}`,
  borderRadius: 2.5,
  position: 'relative',
  overflow: 'hidden',
  height: '100%',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: accent,
  },
});

const HigherOrLowerDashboard = ({
  showHeader = true,
  initialMode = 0,
  showModeTabs = true,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [mainMode, setMainMode] = useState(initialMode);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Overview data
  const [overview, setOverview] = useState(null);
  
  // Sessions data
  const [sessions, setSessions] = useState([]);
  const [sessionsPage, setSessionsPage] = useState(0);
  const [sessionsRowsPerPage, setSessionsRowsPerPage] = useState(25);
  const [sessionsTotal, setSessionsTotal] = useState(0);
  
  // Top players data
  const [topPlayers, setTopPlayers] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    dateRange: 'all', // 'today', 'week', 'month', 'all'
    userType: 'all', // 'all', 'registered', 'guest'
    gameStatus: 'all', // 'all', 'completed', 'abandoned'
    startDate: '',
    endDate: ''
  });
  
  // Session detail dialog
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetailOpen, setSessionDetailOpen] = useState(false);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Configuration state
  const [numberOfCards, setNumberOfCards] = useState(5);
  const [savingConfig, setSavingConfig] = useState(false);
  const [, setConfigSuccess] = useState(false);
  const [configError, setConfigError] = useState(null);

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('auth_token');
  };
  
  // Fetch configuration
  const fetchConfiguration = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/settings/higher_or_lower_cards`);
      if (response.ok) {
        const data = await response.json();
        setNumberOfCards(parseInt(data.setting_value) || 5);
      } else if (response.status === 404) {
        // Setting doesn't exist, use default
        setNumberOfCards(5);
      }
    } catch (error) {
      console.error('Error fetching configuration:', error);
      setNumberOfCards(5);
    }
  };
  
  // Save configuration
  const saveConfiguration = async () => {
    try {
      setSavingConfig(true);
      setConfigError(null);
      setConfigSuccess(false);
      
      const response = await fetch(`${API_BASE}/api/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken()}`
        },
        body: JSON.stringify({
          setting_key: 'higher_or_lower_cards',
          setting_value: numberOfCards.toString(),
          setting_description: 'Number of cards in Higher or Lower game (5-10)'
        })
      });
      
      if (!response.ok) throw new Error('Failed to save configuration');
      
      setConfigSuccess(true);
      enqueueSnackbar('Configuration saved successfully!', { 
        variant: 'success',
        autoHideDuration: 3000
      });
    } catch (error) {
      console.error('Error saving configuration:', error);
      const errorMessage = error.message || 'Failed to save configuration';
      setConfigError(errorMessage);
      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        autoHideDuration: 4000
      });
    } finally {
      setSavingConfig(false);
    }
  };

  // Fetch overview analytics
  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.userType !== 'all') params.append('userType', filters.userType);

      const response = await fetch(`${API_BASE}/api/higher-or-lower/admin/analytics/overview?${params}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch overview');
      
      const data = await response.json();
      setOverview(data.analytics);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch sessions
  const fetchSessions = useCallback(async (page = 0, rowsPerPage = 25) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString()
      });
      
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.userType !== 'all') params.append('userType', filters.userType);
      if (filters.gameStatus !== 'all') params.append('gameStatus', filters.gameStatus);

      const response = await fetch(`${API_BASE}/api/higher-or-lower/admin/sessions?${params}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch sessions');
      
      const data = await response.json();
      setSessions(data.sessions || []);
      setSessionsTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch top players
  const fetchTopPlayers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({ limit: '20' });
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.userType !== 'all') params.append('userType', filters.userType);

      const response = await fetch(`${API_BASE}/api/higher-or-lower/admin/analytics/players?${params}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch top players');
      
      const data = await response.json();
      setTopPlayers(data.players || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch session detail
  const fetchSessionDetail = async (sessionId) => {
    try {
      setLoadingDetail(true);
      const response = await fetch(`${API_BASE}/api/higher-or-lower/admin/sessions/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch session details');
      
      const data = await response.json();
      setSessionDetail(data.session);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Handle date range change
  const handleDateRangeChange = (range) => {
    const today = new Date();
    let startDate = '';
    let endDate = '';

    if (range === 'today') {
      startDate = today.toISOString().split('T')[0];
      endDate = today.toISOString().split('T')[0];
    } else if (range === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 7);
      startDate = weekAgo.toISOString().split('T')[0];
      endDate = today.toISOString().split('T')[0];
    } else if (range === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(today.getMonth() - 1);
      startDate = monthAgo.toISOString().split('T')[0];
      endDate = today.toISOString().split('T')[0];
    }

    setFilters(prev => ({
      ...prev,
      dateRange: range,
      startDate,
      endDate
    }));
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle session detail view
  const handleViewSession = async (session) => {
    setSelectedSession(session);
    setSessionDetailOpen(true);
    await fetchSessionDetail(session.session_id);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setSessionsPage(newPage);
    fetchSessions(newPage, sessionsRowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setSessionsRowsPerPage(newRowsPerPage);
    setSessionsPage(0);
    fetchSessions(0, newRowsPerPage);
  };

  // Refresh data
  const refreshData = () => {
    if (activeTab === 0) {
      fetchOverview();
    } else if (activeTab === 1) {
      fetchSessions(sessionsPage, sessionsRowsPerPage);
    } else if (activeTab === 2) {
      fetchTopPlayers();
    }
  };

  // Load data on mount and filter change
  useEffect(() => {
    if (activeTab === 0) {
      fetchOverview();
    } else if (activeTab === 1) {
      fetchSessions(sessionsPage, sessionsRowsPerPage);
    } else if (activeTab === 2) {
      fetchTopPlayers();
    }
  }, [activeTab, filters, fetchOverview, fetchSessions, fetchTopPlayers, sessionsPage, sessionsRowsPerPage]);
  
  useEffect(() => {
    fetchConfiguration();
  }, []);

  useEffect(() => {
    if (mainMode === 1) fetchConfiguration();
  }, [mainMode]);

  useEffect(() => {
    setMainMode(initialMode);
  }, [initialMode]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const overviewStats = overview
    ? [
        { label: 'Total Games', value: overview.total_games || 0, accent: HOL.gold },
        { label: 'Completed Games', value: overview.completed_games || 0, accent: HOL.higher },
        { label: 'Average Score', value: overview.average_score ? overview.average_score.toFixed(2) : '0.00', accent: '#64b4ff' },
        { label: 'Best Score', value: overview.best_score || 0, accent: HOL.gold },
        { label: 'Unique Players', value: overview.unique_devices || 0, accent: '#c084fc' },
        { label: 'Correct Guesses', value: overview.correct_guesses || 0, accent: HOL.higher },
        { label: 'Wrong Guesses', value: overview.wrong_guesses || 0, accent: HOL.lower },
        { label: 'Tie Rounds', value: overview.tie_rounds || 0, accent: '#64b4ff' },
      ]
    : [];

  const tablePaperSx = {
    ...holPanelSx,
    p: 0,
    overflow: 'hidden',
    '& .MuiTableCell-head': {
      color: HOL.muted,
      fontWeight: 700,
      borderBottom: `1px solid ${HOL.panelBorder}`,
    },
    '& .MuiTableCell-body': {
      borderBottom: '1px solid rgba(255,255,255,0.06)',
    },
  };

  return (
    <>
      {showHeader ? <Header /> : null}
      <Container maxWidth="xl" sx={{ mt: showHeader ? 4 : 1, pb: 4 }}>
        {showModeTabs ? (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{ fontFamily: HOL.fontDisplay, fontWeight: 800, mb: 1.5 }}
            >
              Higher or Lower
            </Typography>
            <Tabs
              value={mainMode}
              onChange={(e, v) => {
                if (v !== mainMode) guardedNavigate(() => setMainMode(v));
              }}
              sx={holTabsSx}
              variant="fullWidth"
            >
              <Tab icon={<AnalyticsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Analytics" />
              <Tab icon={<SettingsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Configuration" />
              <Tab icon={<PlayIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Play with Host" />
            </Tabs>
          </Box>
        ) : null}

        {mainMode === 2 ? <HigherOrLowerSpHostTab /> : null}

        {mainMode === 1 ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ ...holPanelSx, p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2,
                      background: `linear-gradient(160deg, ${HOL.higher}33, ${HOL.gold}22)`,
                      border: `1px solid ${HOL.panelBorder}`,
                    }}
                  >
                    <SettingsIcon sx={{ fontSize: 24, color: HOL.gold }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: HOL.fontDisplay }}>
                      Game Configuration
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Shared settings for Analytics games and Play with Host sessions.
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <InfoIcon sx={{ fontSize: 20, mr: 1, color: HOL.muted }} />
                    Number of Cards
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 4 }}>
                    Configure how many cards will be used in each game session. Players will need to make {numberOfCards - 1} guesses to complete a game.
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel>Number of Cards</InputLabel>
                      <Select
                        value={numberOfCards}
                        label="Number of Cards"
                        onChange={(e) => setNumberOfCards(parseInt(e.target.value))}
                      >
                        <MenuItem value={5}>5 Cards</MenuItem>
                        <MenuItem value={6}>6 Cards</MenuItem>
                        <MenuItem value={7}>7 Cards</MenuItem>
                        <MenuItem value={8}>8 Cards</MenuItem>
                        <MenuItem value={9}>9 Cards</MenuItem>
                        <MenuItem value={10}>10 Cards</MenuItem>
                      </Select>
                    </FormControl>

                    <Button
                      variant="contained"
                      onClick={saveConfiguration}
                      disabled={savingConfig}
                      startIcon={savingConfig ? <CircularProgress size={16} /> : <SaveIcon />}
                      sx={{
                        minWidth: 180,
                        fontWeight: 700,
                        textTransform: 'none',
                        bgcolor: HOL.higherDeep,
                        color: '#0a1206',
                        '&:hover': { bgcolor: HOL.higher },
                      }}
                    >
                      {savingConfig ? 'Saving...' : 'Save Configuration'}
                    </Button>
                  </Box>
                </Box>

                {configError && (
                  <Alert
                    severity="error"
                    onClose={() => setConfigError(null)}
                    sx={{ mb: 2 }}
                  >
                    {configError}
                  </Alert>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ ...holStatCardSx(HOL.gold), p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon sx={{ fontSize: 20, mr: 1, color: HOL.gold }} />
                  Current Settings
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Cards per Game
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 3, fontFamily: HOL.fontDisplay, color: HOL.gold }}>
                    {numberOfCards}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Total Rounds
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: HOL.fontDisplay }}>
                    {numberOfCards - 1}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        ) : null}

        {mainMode === 0 ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5, gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h5" sx={{ fontFamily: HOL.fontDisplay, fontWeight: 800, mb: 0.25 }}>
                  Analytics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Offline game results, sessions, and top players.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={refreshData}
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderColor: HOL.panelBorder,
                  color: '#fff',
                  '&:hover': { borderColor: HOL.higher, bgcolor: 'rgba(168,224,99,0.08)' },
                }}
              >
                Refresh
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Paper sx={{ ...holPanelSx, mb: 2.5 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Date Range</InputLabel>
                    <Select
                      value={filters.dateRange}
                      label="Date Range"
                      onChange={(e) => handleDateRangeChange(e.target.value)}
                    >
                      <MenuItem value="all">All Time</MenuItem>
                      <MenuItem value="today">Today</MenuItem>
                      <MenuItem value="week">Last 7 Days</MenuItem>
                      <MenuItem value="month">Last 30 Days</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>User Type</InputLabel>
                    <Select
                      value={filters.userType}
                      label="User Type"
                      onChange={(e) => handleFilterChange('userType', e.target.value)}
                    >
                      <MenuItem value="all">All Users</MenuItem>
                      <MenuItem value="registered">Registered</MenuItem>
                      <MenuItem value="guest">Guest</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Game Status</InputLabel>
                    <Select
                      value={filters.gameStatus}
                      label="Game Status"
                      onChange={(e) => handleFilterChange('gameStatus', e.target.value)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="abandoned">Abandoned</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Start Date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="End Date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{ ...holTabsSx, mb: 2.5 }}
              variant="fullWidth"
            >
              <Tab label="Overview" />
              <Tab label="Sessions" />
              <Tab label="Top Players" />
            </Tabs>

            {loading && activeTab !== 1 && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }}>
                  Loading...
                </Typography>
              </Box>
            )}

            <Box>
              {activeTab === 0 && overview && (
                <Grid container spacing={2.5}>
                  {overviewStats.map((stat) => (
                    <Grid item xs={12} sm={6} md={3} key={stat.label}>
                      <Card sx={holStatCardSx(stat.accent)}>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom sx={{ fontSize: '0.8rem', letterSpacing: '0.04em' }}>
                            {stat.label}
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: HOL.fontDisplay }}>
                            {stat.value}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}

              {activeTab === 1 && (
                <>
                  {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                      <CircularProgress />
                    </Box>
                  ) : (
                    <>
                      <TableContainer component={Paper} sx={tablePaperSx}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Session ID</TableCell>
                              <TableCell>User</TableCell>
                              <TableCell>Score</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Duration</TableCell>
                              <TableCell>Started At</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sessions.map((session) => (
                              <TableRow key={session.session_id} hover>
                                <TableCell>{session.session_id}</TableCell>
                                <TableCell>
                                  {session.user_type === 'registered'
                                    ? `${session.fname || ''} ${session.lname || ''}`.trim() || session.email
                                    : session.guest_name || 'Guest'}
                                </TableCell>
                                <TableCell>{session.final_score || 0} / {session.total_cards - 1}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={session.game_status}
                                    color={session.game_status === 'completed' ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{formatDuration(session.duration_seconds)}</TableCell>
                                <TableCell>{formatDate(session.started_at)}</TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewSession(session)}
                                    color="primary"
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={sessionsTotal}
                        page={sessionsPage}
                        onPageChange={handleChangePage}
                        rowsPerPage={sessionsRowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                      />
                    </>
                  )}
                </>
              )}

              {activeTab === 2 && (
                <>
                  {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper} sx={tablePaperSx}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Rank</TableCell>
                            <TableCell>Player</TableCell>
                            <TableCell>Games Played</TableCell>
                            <TableCell>Average Score</TableCell>
                            <TableCell>Best Score</TableCell>
                            <TableCell>Total Score</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {topPlayers.map((player, index) => (
                            <TableRow key={index} hover>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                {player.user_type === 'registered'
                                  ? `${player.fname || ''} ${player.lname || ''}`.trim() || 'User'
                                  : player.guest_name || 'Guest'}
                              </TableCell>
                              <TableCell>{player.games_played || 0}</TableCell>
                              <TableCell>{player.average_score ? player.average_score.toFixed(2) : '0.00'}</TableCell>
                              <TableCell>{player.best_score || 0}</TableCell>
                              <TableCell>{player.total_score || 0}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </>
              )}
            </Box>

            <Dialog
              open={sessionDetailOpen}
              onClose={() => setSessionDetailOpen(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                Session Details - #{selectedSession?.session_id}
                <IconButton
                  onClick={() => setSessionDetailOpen(false)}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                {loadingDetail ? (
                  <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                  </Box>
                ) : sessionDetail ? (
                  <Box>
                    <Typography variant="h6" gutterBottom>Session Info</Typography>
                    <Typography>User: {sessionDetail.user_id ? 'Registered User' : 'Guest'}</Typography>
                    <Typography>Score: {sessionDetail.final_score || 0} / {sessionDetail.total_cards - 1}</Typography>
                    <Typography>Status: {sessionDetail.game_status}</Typography>
                    <Typography>Duration: {formatDuration(sessionDetail.duration_seconds)}</Typography>
                    <Typography>Started: {formatDate(sessionDetail.started_at)}</Typography>

                    {sessionDetail.cards && (
                      <>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Cards</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {sessionDetail.cards.map((card, idx) => (
                            <Chip
                              key={idx}
                              label={`${card.card_name}${card.card_suit ? ` (${card.card_suit})` : ''}`}
                              size="small"
                            />
                          ))}
                        </Box>
                      </>
                    )}

                    {sessionDetail.rounds && sessionDetail.rounds.length > 0 && (
                      <>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Rounds</Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Round</TableCell>
                                <TableCell>Current Card</TableCell>
                                <TableCell>Next Card</TableCell>
                                <TableCell>Guess</TableCell>
                                <TableCell>Result</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sessionDetail.rounds.map((round) => (
                                <TableRow key={round.round_id}>
                                  <TableCell>{round.round_number}</TableCell>
                                  <TableCell>{round.current_card_name}</TableCell>
                                  <TableCell>{round.next_card_name}</TableCell>
                                  <TableCell>{round.user_guess}</TableCell>
                                  <TableCell>
                                    <Chip
                                      label={round.is_correct ? 'Correct' : round.is_tie ? 'Tie' : 'Wrong'}
                                      color={round.is_correct ? 'success' : round.is_tie ? 'info' : 'error'}
                                      size="small"
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </>
                    )}
                  </Box>
                ) : (
                  <Typography>No details available</Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSessionDetailOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>
          </>
        ) : null}
      </Container>
    </>
  );
};

export default HigherOrLowerDashboard;
